import React, { createContext, useContext, useState, ReactNode } from "react";

export interface Activity {
  id: string;
  type: "create" | "delete" | "update" | "copy";
  action: "folder" | "file" | "component";
  name: string;
  timestamp: Date;
  description: string;
}

interface ActivityContextType {
  activities: Activity[];
  addActivity: (activity: Omit<Activity, "id" | "timestamp">) => void;
  clearActivities: () => void;
  removeActivity: (id: string) => void;
}

const ActivityContext = createContext<ActivityContextType | undefined>(
  undefined
);

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error("useActivity must be used within an ActivityProvider");
  }
  return context;
};

interface ActivityProviderProps {
  children: ReactNode;
}

export const ActivityProvider: React.FC<ActivityProviderProps> = ({
  children,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);

  const addActivity = (activity: Omit<Activity, "id" | "timestamp">) => {
    const newActivity: Activity = {
      ...activity,
      id: Date.now().toString(),
      timestamp: new Date(),
    };

    setActivities((prev) => [newActivity, ...prev].slice(0, 50)); // Keep last 50 activities
  };

  const clearActivities = () => {
    setActivities([]);
  };

  const removeActivity = (id: string) => {
    setActivities((prev) => prev.filter((activity) => activity.id !== id));
  };

  return (
    <ActivityContext.Provider
      value={{
        activities,
        addActivity,
        clearActivities,
        removeActivity,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
};
